// Cross-tab logout communication utility
class CrossTabLogout {
    constructor() {
        this.channel = null;
        this.storageKey = 'weatec_logout_event';
        this.isDestroyed = false;
        this.processedEvents = new Set(); // Track processed events
        this.init();
    }

    init() {
        // Try to use BroadcastChannel API first (modern browsers)
        if (typeof BroadcastChannel !== 'undefined') {
            try {
                this.channel = new BroadcastChannel('weatec_auth_channel');
            } catch (error) {
                console.warn('Cross-tab logout: BroadcastChannel not supported, falling back to localStorage events');
            }
        }
    }

    // Broadcast logout event to other tabs
    broadcastLogout() {
        if (this.isDestroyed) {
            return;
        }

        const logoutEvent = {
            type: 'LOGOUT',
            timestamp: Date.now(),
            source: 'user_action',
            tabId: this.generateTabId(),
            eventId: this.generateEventId() // Add unique event ID
        };

        // Use BroadcastChannel if available
        if (this.channel) {
            try {
                this.channel.postMessage(logoutEvent);
            } catch (error) {
                console.error('Cross-tab logout: Failed to broadcast via BroadcastChannel:', error);
            }
        }

        // Fallback to localStorage event
        try {
            localStorage.setItem(this.storageKey, JSON.stringify(logoutEvent));
            // Trigger storage event by removing and re-adding
            localStorage.removeItem(this.storageKey);
        } catch (error) {
            console.error('Cross-tab logout: Failed to broadcast via localStorage:', error);
        }
    }

    // Listen for logout events from other tabs
    onLogout(callback) {
        if (this.isDestroyed) {
            return () => {};
        }

        if (typeof callback !== 'function') {
            return () => {};
        }

        // Listen for BroadcastChannel messages
        if (this.channel) {
            this.channel.onmessage = (event) => {
                if (event.data && event.data.type === 'LOGOUT') {
                    // Don't process logout events from the same tab
                    if (event.data.tabId !== this.generateTabId()) {
                        this.processLogoutEvent(event.data, callback, 'BroadcastChannel');
                    }
                }
            };
        }

        // Listen for localStorage events (fallback)
        const handleStorageChange = (event) => {
            if (event.key === this.storageKey && event.newValue) {
                try {
                    const logoutEvent = JSON.parse(event.newValue);
                    if (logoutEvent.type === 'LOGOUT') {
                        // Don't process logout events from the same tab
                        if (logoutEvent.tabId !== this.generateTabId()) {
                            this.processLogoutEvent(logoutEvent, callback, 'localStorage');
                        }
                    }
                } catch (error) {
                    console.error('Cross-tab logout: Failed to parse logout event:', error);
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);

        return () => {
            if (this.channel) {
                this.channel.onmessage = null;
            }
            window.removeEventListener('storage', handleStorageChange);
        };
    }

    // Process logout event with deduplication
    processLogoutEvent(logoutEvent, callback, source) {
        const eventKey = `${logoutEvent.eventId}_${logoutEvent.tabId}`;
        
        // Check if we've already processed this event
        if (this.processedEvents.has(eventKey)) {
            return;
        }

        // Mark event as processed
        this.processedEvents.add(eventKey);
        
        // Clean up old events (keep only last 10 events)
        if (this.processedEvents.size > 10) {
            const eventsArray = Array.from(this.processedEvents);
            this.processedEvents.clear();
            eventsArray.slice(-5).forEach(event => this.processedEvents.add(event));
        }

        callback(logoutEvent);
    }

    // Generate a unique tab ID to prevent processing own events
    generateTabId() {
        if (!this._tabId) {
            this._tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        }
        return this._tabId;
    }

    // Generate a unique event ID
    generateEventId() {
        return `event_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    // Cleanup
    destroy() {
        this.isDestroyed = true;
        this.processedEvents.clear();
        if (this.channel) {
            try {
                this.channel.close();
            } catch (error) {
                console.error('Cross-tab logout: Error closing BroadcastChannel:', error);
            }
            this.channel = null;
        }
    }
}

const crossTabLogout = new CrossTabLogout();

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
    crossTabLogout.destroy();
});

export default crossTabLogout;
